
import { h, Fragment } from 'preact'
import { useState, useEffect } from 'preact/hooks'
import { useTransition, useEasing, lerp, easeInOutQuint } from '@util/transition'
import { withHydration } from '@util/hydrate'
import { isClient } from '@util/env'
import { paragraphs } from '@util/typography'
import Container from '@components/container'
import { ButtonLabel, ButtonText } from '@components/button'
import { ArrowRight, ArrowLeftRight, Close } from '@components/icons'

import './input.less'
import './main.less'


const WhatWeDo = ({ items = { whatWeDo : [], howWeDoit : [] } }) => {

	const [filteredBy, setFilteredBy] = useState(null)
	const [left, setLeft, setLeftImmediately] = useEasing(0, 350)

	const onSetFilteredBy = (name, showRight = false) => {
		setFilteredBy(name)
		showRight && setLeft(1)
	}

	const currentWhatWeDo = items.whatWeDo.filter((item) => item.title === filteredBy)[0] ?? null
	const howWeDoItFiltered = currentWhatWeDo ? items.howWeDoIt.filter((item) => currentWhatWeDo.howWeDoIt.indexOf(item.title) !== -1) : items.howWeDoIt

	const inputHandler = (e) => {
		const pc = parseInt(e.currentTarget.value) / 100
		setLeftImmediately(pc)
	}

	const changeHandler = (e) => {
		const pc = parseInt(e.currentTarget.value) / 100
		setLeft((pc >= 0.5) ? 1 : 0)
	}

	return (
		<section class="wwd margin-y--large">
			<Container narrow>
				<div class="text--x-large text-center">
					<div className="wwd-input">
						<button 
							className={`wwd-input__tab wwd-input__tab--left ${left < 0.5 ? 'wwd-input__tab--active' : ''}`}
							style={{ opacity : lerp(0.5, 1, 1-left) }}
							onClick={() => setLeft(0)}
						>What we do</button>
						
						<div class="wwd-input__plus">+</div>
						
						<button 
							className={`wwd-input__tab wwd-input__tab--right ${left >= 0.5 ? 'wwd-input__tab--active' : ''}`}
							style={{ opacity : lerp(0.5, 1, left) }}
							onClick={() => setLeft(1)}
						>How we do it</button>

						<input 
							class="wwd-input__range" 
							type="range" min="0" max="100" steps="any" 
							value={left * 100} 
							onChange={changeHandler} 
							onInput={inputHandler} />
					</div>
				</div>
			</Container>
			<Container>
				<div class="wwd__main">
					<div className="wwd__left">
						<div className="wwd__inner text--large">
							<ItemSet animatedIn={1-left} items={ items.whatWeDo } filterWhatWeDo={(name) => onSetFilteredBy(name, true)} />
						</div>
					</div>
					<div className="wwd__right">
						<div className="wwd__inner text--large">
								<ItemSet flip={true} animatedIn={left} items={ howWeDoItFiltered } filteredByName={filteredBy} filterWhatWeDo={(name) => onSetFilteredBy(name)} />
						</div>
					</div>
				</div>
				
			</Container>
		</section>
	)
}



const ItemSet = ({ 
	items = [], 
	animatedIn = 0, 
	flip = false, 
	filteredByName = null,
	filterWhatWeDo = itemName => {}
}) => {

	const [openItem, setOpenItem] = useState(null)
	const [animationPercent, setAnimationPercent] = useState(animatedIn)
	
	useEffect(() => {
		setAnimationPercent(animatedIn)
	}, [animatedIn])

	const onFilterClear = () => {
		filterWhatWeDo(null)
		setOpenItem(null)
	}

	return <>
		{ filteredByName && 
			<div class="wwd-filters" style={{
				transform:`
					rotate(${lerp(-5, 0, animationPercent)}deg)
					translateX(${lerp(3, 0, animationPercent)}em)
				`,
				transformOrigin: '100% 100%',
				opacity: lerp(0, 1, easeInOutQuint(animationPercent)),
				pointerEvents: animationPercent >= 0.5 ? 'auto' : 'none'
			}}>
				<span class="wwd-filters__label">Filtered by:</span>
				<ButtonLabel active={true} rounded icon={Close} icon-left={true} onClick={ onFilterClear }>{ filteredByName }</ButtonLabel>
				<ButtonLabel rounded onClick={ onFilterClear }>Clear</ButtonLabel>
			</div>
		}
		{items.map((item, i) => {

			const move = -2
			const twist = 8
			const index = i + 1
			const neg = !flip ? -1 : 1
			const showButton = !!item.howWeDoIt?.length

			const lerpIterate = (from, to, delay, time) => lerp(from * delay + delay, to, time)

			return (
				(animationPercent > 0) && <Item 
					open={openItem === item.title} 
					onOpen={() => setOpenItem(item.title)} 
					onClose={() => setOpenItem(null)} 
					title={item.title}
					description={item.description}
					style={{
						transform:`
							rotate(${lerpIterate(-neg * twist, 0, index/4, animationPercent)}deg)
							translateX(${lerpIterate(-neg * move, 0, index/10, animationPercent)}em)
						`,
						transformOrigin: flip ? '100% 100%' : '0 100%',
						opacity: lerp(0, 1, easeInOutQuint(animationPercent)),
						pointerEvents: animationPercent >= 0.5 ? 'auto' : 'none'
					}}>
					{ showButton && <ButtonText onClick={() => filterWhatWeDo(item.title) }>How we do it</ButtonText>}
				</Item>
			)
		})}
	</>
}



const Item = ({ 
	id = '', 
	title = '',  
	description = '', 
	open = false,
	onOpen = () => {},  
	onClose = () => {},
	children, 
	...props
}) => {

	const [isOpen, setOpen] = useState(open)
	useEffect(() => setOpen(!!open), [open])

	const clickHandler = () => isOpen ? onClose(id) : onOpen(id)

	return <div class={`wwd-button${isOpen && ' wwd-button--open' || '' }`} {...props}>
		<ButtonLabel 
			active={isOpen} 
			icon={isOpen && Close} 
			icon-left={true} 
			onClick={clickHandler}
		>
			{title}
		</ButtonLabel>

		{ isOpen && <div class="wwd-desc">
			<div className="wwd-desc__inner first-last">
				{ paragraphs(description) }
				{ children }
			</div>
		</div> }
	</div>
}



export default withHydration(WhatWeDo)